import React, {useCallback, useEffect, useState, useRef} from 'react';
import {PctDouble} from '@store/@types/util';
import User from 'records/User';
import {CommentColor, UserComment} from 'records/UserComment';
import {getPlayerProps, PlayerRef, registerPlayerEvents} from '@components/call-card/Player.utils';
import Button from '@material-ui/core/Button';
import {CallAsset, DragStatus} from '@store/call-card/CallCard.types';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import formatSecondsShort from 'utils/formatSecondsShort';
import {PlayerControls} from './PlayerControls';
import {SetState} from '@store/@types/util/React';
import Loading from 'components/pure/utils/Loading';
import {submitComment} from './Comment.utils';
import UserTaggableInput from 'components/pure/utils/form/user-taggable-input/UserTaggableInput';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import Slider from '@material-ui/core/Slider';
import './CommentEditDialog.sass';
import {Call} from '@store/call-card/Call';
import {SeekTrack} from './SeekTrack';
import {registerEvents} from 'utils/registerEvents';
import {registerListenEvents} from './registerListenEvents';
import cn from 'classnames';

// Number of seconds to pad on each side when toggling to range comment
const RANGE_BUFFER = 120;

interface CommentEditDialogProps {
  call: Call;
  initialComment: UserComment;
  callAsset: CallAsset | undefined;
  onClose: () => void;
  dragStatus: DragStatus;
  setDragStatus: SetState<DragStatus>;
}

export const CommentEditDialog: React.FC<CommentEditDialogProps> = ({
  call,
  initialComment,
  callAsset,
  onClose,
  setDragStatus,
  dragStatus,
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [playerRef, setPlayerRef] = useState<PlayerRef>(null);
  const [{duration, currentTime}, setPlayerProps] = useState(getPlayerProps(playerRef, call));
  const [editComment, setEditComment] = useState(initialComment);
  const [submitting, setSubmitting] = useState(false);
  const setTimePct = useCallback(
    (pct: PctDouble) => {
      if (playerRef) {
        playerRef.currentTime = pct * call.duration;
      }
    },
    [call, playerRef]
  );
  const edit = !!initialComment.id;

  // Keeps track of where the user places their instant comment. If we toggle between range/instant we keep track of this so they dont lose the place
  const [commentTime, setCommentTime] = useState(initialComment.startTime);

  // These events cause rerenders via playerProps. PlayerControls won't re-render without this
  useEffect(
    () => registerPlayerEvents(playerRef, call, setPlayerProps),
    [playerRef, call, setPlayerProps]
  );

  // Listens should still be created for time played in this modal
  useEffect(() => registerListenEvents(playerRef, call.id, call.duration), [call, playerRef]);

  // Set time to the point we opened from, after media finishes initial loading
  useEffect(
    () =>
      playerRef
        ? registerEvents(playerRef, [
            [
              'loadeddata',
              () => {
                return (playerRef.currentTime = initialComment.startTime);
              },
            ],
          ])
        : undefined,
    [playerRef, initialComment.startTime]
  );

  const onSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editComment.message) {
        await submitComment(call.id, duration, editComment, initialComment);
        onClose();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  }, [editComment, call.id, duration, onClose, initialComment, submitting, setSubmitting]);

  const classes = useStyles({color: editComment.color!});

  return (
    <Dialog className="comment-edit-dialog" maxWidth={false} open={true} onClose={onClose}>
      <div className="comment-dialog-content">
        <h1>{edit ? 'Edit' : 'Add'} Comment</h1>
        <div className="video-wrapper">
          <MediaElement callAsset={callAsset} setPlayerRef={setPlayerRef} playerRef={playerRef} />
          <PlayerControls playerRef={playerRef} videoHidden={false} showVideo={() => null} />
        </div>
        {playerRef && (
          <div className="comment-track">
            {editComment.isRange() ? (
              <>
                <div className="range-text">
                  Comment from {formatSecondsShort(editComment.startTime)} to{' '}
                  {formatSecondsShort(editComment.endTime)}
                </div>

                <RangeCommentSlider
                  classes={classes}
                  color="secondary"
                  max={duration}
                  onChange={(_, val) => {
                    if (typeof val === 'number') return;
                    const [start, end] = val;
                    return setEditComment(c => c.set('startTime', start).set('endTime', end));
                  }}
                  defaultValue={[editComment.startTime, editComment.endTime]}
                  ThumbComponent={SliderThumb}
                />
              </>
            ) : (
              <InstantCommentSlider
                color="secondary"
                max={duration}
                onChange={(_, val) => {
                  if (typeof val === 'number') {
                    setCommentTime(val);
                    return setEditComment(c => c.set('startTime', val).set('endTime', val));
                  }
                }}
                defaultValue={editComment.startTime}
                ThumbComponent={SliderThumb}
              />
            )}
            <div className="seek-overlay" ref={trackRef}>
              <SeekTrack
                setTimePct={setTimePct}
                duration={duration}
                currentTime={currentTime}
                trackRef={trackRef.current}
                showPlayerTick
                setDragStatus={setDragStatus}
                dragStatus={dragStatus}
                commentModal
              />
            </div>
          </div>
        )}
        <div className="comment-wrapper">
          <RadioGroup className="comment-type-toggle">
            <div
              className="radio-row"
              onClick={() =>
                setEditComment(e => e.set('startTime', commentTime).set('endTime', commentTime))
              }>
              <Radio color="primary" checked={!editComment.isRange()} />
              <label>Comment @{formatSecondsShort(commentTime)}</label>
            </div>
            <div
              className="radio-row"
              onClick={() =>
                setEditComment(e =>
                  e
                    .set('startTime', Math.max(0, commentTime - RANGE_BUFFER))
                    .set('endTime', Math.min(duration, commentTime + RANGE_BUFFER))
                )
              }>
              <Radio color="primary" checked={editComment.isRange()} />
              <label>Comment on a range</label>
            </div>
          </RadioGroup>
          <div className="comment-text">
            <div className="comment-input-header">
              <h4>Comment text</h4>
              {editComment.isRange() && (
                <div className="color-select-row">
                  {Object.values(CommentColor).map(color => (
                    <ColorSelect
                      key={color}
                      color={color}
                      activeColor={editComment.color!}
                      setColor={color => setEditComment(c => c.set('color', color))}
                    />
                  ))}
                </div>
              )}
            </div>
            <UserTaggableInput
              autoFocus
              onChange={(value: string, taggedUsers: User[]) => {
                setEditComment(c =>
                  c.set('message', value).set(
                    'taggedUsers',
                    taggedUsers.map(u => u.id)
                  )
                );
              }}
              value={editComment.message}
            />
          </div>
        </div>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={editComment.message === '' || submitting}
            variant="contained"
            onClick={onSubmit}
            color="primary">
            Save
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

interface MediaElementProps {
  callAsset: CallAsset | undefined;
  setPlayerRef: SetState<PlayerRef>;
  playerRef: PlayerRef;
}

function MediaElement({callAsset, setPlayerRef, playerRef}: MediaElementProps) {
  if (!callAsset) return <Loading />;

  // This shouldn't be possible given that you wont be able to comment on it a missing call
  if (callAsset.type === 'MISSING_ASSET') {
    return (
      <div className="call-asset-missing-error">
        We were unable to find a recording for this call
      </div>
    );
  }

  const src = `/assets/download/${callAsset.id}`;
  return callAsset.type === 'OUTPUT_AUDIO' ? (
    <audio
      src={src}
      ref={r => {
        setPlayerRef(r);
      }}
    />
  ) : (
    <video
      webkit-playsinline
      playsInline
      src={src}
      onClick={() => (playerRef?.paused ? playerRef?.play() : playerRef?.pause())}
      ref={r => {
        setPlayerRef(r);
      }}
    />
  );
}

const RangeCommentSlider = withStyles(theme => ({
  root: {
    height: 20,
    padding: '5px 0',
  },
  track: {
    opacity: 0.5,
    height: 20,
  },
  rail: {
    color: '#fff',
    height: 20,
    border: `1px solid ${theme.palette.icon.grey}`,
    borderRadius: 3,
    boxSizing: 'border-box',
  },
}))(Slider);

const useStyles = makeStyles({
  track: {
    backgroundColor: (props: {color: string}) => props.color,
  },
});

const InstantCommentSlider = withStyles(() => ({
  track: {
    opacity: 0,
  },
}))(RangeCommentSlider);

function SliderThumb(props: any) {
  return (
    <span {...props} className={`${props.className} slider-thumb`}>
      <div className="thumb-line" />
    </span>
  );
}

interface ColorSelectProps {
  setColor: SetState<string>;
  color: string;
  activeColor: string;
}

function ColorSelect({color, setColor, activeColor}: ColorSelectProps) {
  return (
    <div
      style={{backgroundColor: color}}
      className={cn('color-select', {active: activeColor === color})}
      onClick={() => setColor(color)}></div>
  );
}
